import type { ReactNode } from "react";

import { ctaButton } from "@/components/landing/cta-styles";
import { LandingCta } from "@/components/landing/landing-cta";

import { eyebrow, h2, lede, reveal, revealAfter } from "./styles";

/**
 * The recurring section furniture: an eyebrow + heading (+ lede) that rise in
 * with a fixed stagger, and the CTA that closes every section. One place for
 * the 100/200 ms rhythm and the button-with-note layout.
 */

export function SectionHeader({
  eyebrow: eyebrowText,
  title,
  lede: ledeText,
  titleClassName = "",
  ledeClassName = "",
}: {
  eyebrow: ReactNode;
  title: ReactNode;
  lede?: ReactNode;
  titleClassName?: string;
  ledeClassName?: string;
}) {
  return (
    <>
      <div className={`${eyebrow} ${reveal}`}>{eyebrowText}</div>
      <h2 className={`${h2} ${reveal} ${titleClassName}`} style={revealAfter(100)}>
        {title}
      </h2>
      {ledeText !== undefined && (
        <p className={`${lede} ${reveal} ${ledeClassName}`} style={revealAfter(200)}>
          {ledeText}
        </p>
      )}
    </>
  );
}

/** A lede placed apart from its header (the film and cases headers put it in
 *  a second column). Same style and stagger. */
export function SectionLede({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <p className={`${lede} ${reveal} ${className}`} style={revealAfter(200)}>
      {children}
    </p>
  );
}

export function FlowCta({
  href,
  source,
  note,
  className = "",
  children,
}: {
  href: string;
  /** Analytics source, `landing-smb-<section>`. */
  source: string;
  note?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={`mt-[clamp(36px,4vw,56px)] flex flex-wrap items-center gap-x-[22px] gap-y-3.5 ${reveal} ${className}`}>
      <LandingCta href={href} source={source} className={ctaButton}>
        {children}
      </LandingCta>
      {note !== undefined && <span className="max-w-[26em] text-[15px] text-foreground-muted">{note}</span>}
    </div>
  );
}
